import type { DropdownOption } from '@sparklayer-io/spark-dash-ui/components/ui/dropdown/types';
import { Alert, Button, Card, Dropdown, LearnMoreLink, Text } from '@ui/themed';
import { t } from 'i18next';
import { useEffect } from 'react';
import {
  featureRestricted,
  features,
  getActiveBillingPlanKey,
} from '../../../../constants/features';
import { INTERCOM_TRIGGER } from '../../../../constants/intercom';
import { useEnv } from '../../../../context/env-context';
import { useSettings } from '../../../../context/settings-context';
import { useStockLocations } from '../../../../hooks/use-stock-locations';
import { SETTING_KEY_TO_LABEL } from '../../constants';
import { useCustomerGroupBuilderForm } from '../../hooks/use-customer-group-builder-form';
import { ResetSettingsButton } from './reset-settings-button';

export function StockLocationCard() {
  const form = useCustomerGroupBuilderForm();
  const { stockLocations, isLoading } = useStockLocations();
  const settings = useSettings();
  const env = useEnv();

  const isMultiStockEnabled = settings.useMultiLocationStock;

  const isMultiStockRestricted = featureRestricted(
    features.MULTI_STOCK_LOCATION_SUPPORT,
    getActiveBillingPlanKey(env.billing.plan_key),
  );

  const stockLocationOptions = stockLocations.map(
    (location): DropdownOption => ({ label: location.name, value: location.id }),
  );

  const defaultStockLocationId = stockLocations.find(
    (location) => location.external_id === 'default',
  )?.id;

  // useEffect to watch if feature has been turned on, for fast response
  useEffect(() => {
    form.setFieldValue(
      'settings.multiLocationStockConfig.disabled',
      !isMultiStockEnabled || undefined,
    );
  }, [isMultiStockEnabled]);

  return (
    <Card showBorder>
      <Card.Header collapsible headingLevel="h2">
        {SETTING_KEY_TO_LABEL.multiLocationStockConfig}
      </Card.Header>
      <Card.Body>
        <Text variant="secondary">
          {t('features:customer_group.components.stock_location_card.description')}{' '}
          <LearnMoreLink link="https://docs.sparklayer.io/customers#jyPsM" />
        </Text>
        {!isLoading &&
          (!isMultiStockEnabled ? (
            <Alert
              variant="info"
              title={t(
                `labels:${isMultiStockRestricted ? 'feature_restricted' : 'feature_not_enabled'}`,
              )}
              headingLevel="h3"
            >
              {t(
                `features:customer_group.components.stock_location_card.errors.${isMultiStockRestricted ? 'restricted' : 'not_enabled'}`,
              )}
            </Alert>
          ) : (
            <fieldset className="flex w-1/2 flex-col items-stretch gap-2">
              <Text variant="primary">
                {t('features:customer_group.components.stock_location_card.label')}
              </Text>
              <form.Field name="settings.multiLocationStockConfig">
                {(field) => (
                  <Dropdown
                    options={stockLocationOptions}
                    value={
                      field.state.value?.allowedStockLocationIds.at(0) ??
                      defaultStockLocationId ??
                      ''
                    }
                    onChange={(id) => {
                      if (id === defaultStockLocationId) {
                        field.handleChange({ useDefault: true, allowedStockLocationIds: [] });
                        return;
                      }
                      field.handleChange({ useDefault: false, allowedStockLocationIds: [id] });
                    }}
                  />
                )}
              </form.Field>
            </fieldset>
          ))}
        <div className="flex justify-start gap-3">
          {!isMultiStockEnabled && (
            <div className={INTERCOM_TRIGGER.inventoryFeature}>
              <Button variant="secondary">{t('buttons:speak_to_team')}</Button>
            </div>
          )}
          <form.Field name="settings.multiLocationStockConfig">
            {(field) => <ResetSettingsButton field={field} />}
          </form.Field>
        </div>
      </Card.Body>
    </Card>
  );
}
